import { useLocation, Link } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { cn } from "@/modules/shared/lib/utils";

// Mapeamento de rotas para labels e hierarquia
const routeConfig: Record<string, { label: string; parent?: string }> = {
  "/dashboard": { label: "Visão Geral" },
  
  // Cadastros
  "/clientes": { label: "Clientes" },
  "/profissionais": { label: "Time" },
  
  // Operações
  "/alocacoes": { label: "Alocações", parent: "/profissionais" },
  "/historico-alocacoes": { label: "Histórico", parent: "/profissionais" },
  "/squads": { label: "Squads" },
  "/projetos": { label: "Projetos" },
  
  "/dashboard-engajamento": { label: "Painel Gestores" },
  "/cultura": { label: "Cultura" },
  "/cultura/configurar": { label: "Configurar" },
  "/checkins": { label: "Performance" },
  "/planos-de-acao": { label: "Planos de Ação" },
  "/resumo-mensal": { label: "Resumo Mensal" },
  "/metricas-squad": { label: "Open Metrics" },
  
  // Comercial
  "/revenue": { label: "Cockpit Comercial" },
  
  "/rate-card": { label: "Rate Card" },
  "/calculadora-comercial": { label: "Calculadora" },
  
  // Portal
  "/portal": { label: "Portal Cliente" },
  "/portal/planos": { label: "Planos de Ação", parent: "/portal" },
  "/portal/recursos": { label: "Recursos", parent: "/portal" },
  "/portal/resumo": { label: "Resumo", parent: "/portal" },
  "/portal/vagas": { label: "Abrir Vaga", parent: "/portal" },
  "/vagas": { label: "Vagas Solicitadas", parent: "/dashboard" },
  "/portal/stack": { label: "Stack", parent: "/portal" },
  "/portal/match": { label: "Match", parent: "/portal" },
  
  // Configurações
  "/configuracoes": { label: "Configurações" },
};

// Seções para agrupar rotas
const sectionMap: Record<string, string> = {
  "/clientes": "Cadastros",
  "/profissionais": "Cadastros",
  "/alocacoes": "Gestão",
  "/historico-alocacoes": "Gestão",
  "/squads": "Operações",
  "/projetos": "Operações",
  "/dashboard-engajamento": "Análise",
  "/cultura": "Acompanhamento",
  "/checkins": "Acompanhamento",
  "/planos-de-acao": "Acompanhamento",
  "/resumo-mensal": "Acompanhamento",
  "/metricas-squad": "Acompanhamento",
  
  "/rate-card": "Comercial",
  "/calculadora-comercial": "Comercial",
  "/portal": "Portal",
  "/configuracoes": "Sistema",
};

interface BreadcrumbItem {
  label: string;
  href?: string;
}

export function Breadcrumbs() {
  const location = useLocation();
  const pathname = location.pathname;

  // Não mostrar breadcrumbs na home
  if (pathname === "/dashboard") {
    return null;
  }

  const buildBreadcrumbs = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];
    
    // Sempre começar com Home
    items.push({ label: "Home", href: "/dashboard" });

    // Encontrar a rota base (sem IDs dinâmicos)
    let basePath = pathname;
    
    // Detectar rotas dinâmicas como /clientes/:id, /profissionais/:id, etc.
    const dynamicMatch = pathname.match(/^(\/[^/]+)\/([^/]+)$/);
    if (dynamicMatch) {
      basePath = dynamicMatch[1];
    }

    // Adicionar seção se existir
    const section = Object.entries(sectionMap).find(([route]) => 
      pathname.startsWith(route)
    );
    if (section) {
      items.push({ label: section[1] });
    }

    // Verificar se há parent
    const config = routeConfig[basePath] || routeConfig[pathname];
    if (config?.parent) {
      const parentConfig = routeConfig[config.parent];
      if (parentConfig) {
        items.push({ label: parentConfig.label, href: config.parent });
      }
    }

    // Adicionar rota atual
    if (config) {
      items.push({ label: config.label, href: dynamicMatch ? basePath : undefined });
    }

    // Se for rota dinâmica, adicionar "Detalhes"
    if (dynamicMatch) {
      items.push({ label: "Detalhes" });
    }

    return items;
  };

  const breadcrumbs = buildBreadcrumbs();

  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        const isFirst = index === 0;

        return (
          <div key={index} className="flex items-center gap-1">
            {index > 0 && (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
            )}
            {item.href && !isLast ? (
              <Link
                to={item.href}
                className={cn(
                  "hover:text-foreground transition-colors",
                  isFirst && "flex items-center gap-1"
                )}
              >
                {isFirst && <Home className="h-3.5 w-3.5" />}
                <span className={cn(isFirst && "sr-only")}>{item.label}</span>
              </Link>
            ) : (
              <span
                className={cn(
                  isLast ? "text-foreground font-medium" : "text-muted-foreground/70",
                  isFirst && "flex items-center gap-1"
                )}
              >
                {isFirst && <Home className="h-3.5 w-3.5" />}
                <span className={cn(isFirst && "sr-only")}>{item.label}</span>
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
}